import { Platform, Dimensions } from 'react-native';
import * as Device from 'expo-device';
import * as Application from 'expo-application';
import Constants from 'expo-constants';
import { isNative } from '@/lib/platform';
import type { DeviceInfo } from './types';

export function getDeviceInfo(): DeviceInfo {
  const { width, height } = Dimensions.get('window');

  if (isNative) {
    return {
      os: `${Platform.OS} ${Device.osVersion ?? ''}`.trim(),
      model: Device.modelName ?? undefined,
      brand: Device.brand ?? undefined,
      screenWidth: width,
      screenHeight: height,
    };
  }

  // Web
  return {
    os: 'web',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    screenWidth: width,
    screenHeight: height,
  };
}

export function getAppVersion(): string {
  if (isNative) {
    return Application.nativeApplicationVersion ?? Constants.expoConfig?.version ?? 'unknown';
  }
  return Constants.expoConfig?.version ?? 'unknown';
}

/**
 * What this app is ACTUALLY running: marketing version, native build number, and
 * — critically — which JS bundle.
 *
 * WHY THIS EXISTS
 *   Settings displayed a hardcoded string, `Inverio v1.0.1`, derived from
 *   nothing. So when an OTA update did not appear to take effect there was no
 *   way, from the device, to answer the only question that matters: is this the
 *   bundle that shipped inside the binary, or one fetched since?
 *
 *   `isEmbeddedLaunch` answers it. TRUE means the app is running the JS that was
 *   compiled into the build — no update has been applied, whatever the server
 *   says it is serving. FALSE, with an `updateId`, means an OTA is live and that
 *   id can be matched against `eas update:list`.
 *
 *   Publishing is not delivering, and delivering is not applying. This reports
 *   the last of the three, which is the only one the user can see.
 */
export function getBuildInfo(): {
  version: string;
  nativeBuild?: string;
  /** true = running the bundle compiled into the binary (no OTA applied) */
  isEmbedded?: boolean;
  /** Short id of the applied update, when one is applied. */
  updateId?: string;
  runtimeVersion?: string;
  channel?: string;
} {
  const version = getAppVersion();
  if (!isNative) return { version };

  try {
    // Required lazily: some builds legitimately ship without expo-updates, and
    // a missing module must not break the Settings screen.
    const Updates = require('expo-updates');
    return {
      version,
      nativeBuild: Application.nativeBuildVersion ?? undefined,
      isEmbedded: Updates.isEmbeddedLaunch ?? undefined,
      updateId: Updates.updateId ? String(Updates.updateId).slice(0, 8) : undefined,
      runtimeVersion: Updates.runtimeVersion ? String(Updates.runtimeVersion).slice(0, 8) : undefined,
      channel: Updates.channel ?? undefined,
    };
  } catch {
    return { version, nativeBuild: Application.nativeBuildVersion ?? undefined };
  }
}

/** One line for the Settings footer — short, but unambiguous about the bundle. */
export function formatBuildLine(): string {
  const b = getBuildInfo();
  const parts = [`Inverio v${b.version}`];
  if (b.nativeBuild) parts.push(`(${b.nativeBuild})`);
  if (b.isEmbedded === true) parts.push('· embedded bundle');
  else if (b.updateId) parts.push(`· update ${b.updateId}`);
  if (b.channel) parts.push(`· ${b.channel}`);
  return parts.join(' ');
}
